import React from 'react';
import { calculateDaysUntilPayment, calculatePaymentDate } from '../../utils/paymentConditionUtils';
import {
  VENTAS_PAYMENT_METHODS,
  ventasPaymentMethodNeedsOperation,
} from '../../utils/ventasSalesConstants';

const PaymentConditionSection = ({
  issueDate,
  paymentConditionType,
  paymentConditionDays,
  paymentConditionDate,
  paymentMethod,
  operationNumber,
  onTypeChange,
  onDaysChange,
  onDateChange,
  onPaymentMethodChange,
  onOperationNumberChange,
  showPaymentMethod = false,
  readOnly = false,
  className = '',
}) => {
  const paymentDate = calculatePaymentDate({
    issueDate,
    paymentConditionType,
    paymentConditionDays,
    paymentConditionDate,
  });
  const daysUntil = calculateDaysUntilPayment({
    issueDate,
    paymentConditionType,
    paymentConditionDays,
    paymentConditionDate,
  });

  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6 h-full ${className}`.trim()}>
      <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
        <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Condición de Pago
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Tipo de Condición</label>
          <div className="flex flex-wrap gap-4">
            {[
              { value: 'cash', label: 'Al contado' },
              { value: 'days', label: 'Cada X días (máx. 3 meses)' },
              { value: 'date', label: 'Fecha específica' },
            ].map((opt) => (
              <label key={opt.value} className="flex items-center">
                <input
                  type="radio"
                  name="payment_condition_type"
                  value={opt.value}
                  checked={paymentConditionType === opt.value}
                  onChange={() => onTypeChange(opt.value)}
                  disabled={readOnly}
                  className="mr-2 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {paymentConditionType === 'days' && (
          <div>
            <label htmlFor="payment_condition_days" className="block text-sm font-medium text-gray-700 mb-2">
              Días para pagar
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                id="payment_condition_days"
                value={paymentConditionDays}
                onChange={(e) => {
                  const days = parseInt(e.target.value, 10) || 0;
                  const validDays = Math.min(Math.max(1, days), 90);
                  onDaysChange(validDays);
                }}
                min="1"
                max="90"
                disabled={readOnly}
                className="w-32 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
              <span className="text-sm text-gray-600">días (máximo 90 días / 3 meses)</span>
            </div>
          </div>
        )}

        {paymentConditionType === 'date' && (
          <div>
            <label htmlFor="payment_condition_date" className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Pago
            </label>
            <input
              type="date"
              id="payment_condition_date"
              value={paymentConditionDate || ''}
              onChange={(e) => onDateChange(e.target.value)}
              min={issueDate || undefined}
              disabled={readOnly}
              className="w-full md:w-64 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        )}

        {showPaymentMethod && (
          <div className="space-y-4">
            <div>
              <label htmlFor="payment_method" className="block text-sm font-medium text-gray-700 mb-2">
                Medio de pago
                {paymentConditionType === 'cash' && <span className="text-red-500"> *</span>}
              </label>
              <select
                id="payment_method"
                value={paymentMethod || ''}
                onChange={(e) => onPaymentMethodChange?.(e.target.value)}
                disabled={readOnly}
                className="w-full md:w-72 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">Seleccionar medio de pago…</option>
                {VENTAS_PAYMENT_METHODS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {ventasPaymentMethodNeedsOperation(paymentMethod) && (
              <div>
                <label htmlFor="operation_number" className="block text-sm font-medium text-gray-700 mb-2">
                  Número de operación
                  <span className="text-red-500"> *</span>
                </label>
                <input
                  type="text"
                  id="operation_number"
                  value={operationNumber || ''}
                  onChange={(e) => onOperationNumberChange?.(e.target.value)}
                  disabled={readOnly}
                  placeholder="Ej. 123456789"
                  maxLength={100}
                  className="w-full md:w-72 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            )}
          </div>
        )}

        {paymentDate && (
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-600 mb-1">Fecha de Pago Calculada</div>
                <div className="text-lg font-semibold text-teal-700">
                  {paymentDate.toLocaleDateString('es-PE', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                  {paymentConditionType === 'cash' && (
                    <span className="ml-2 text-sm text-teal-600 font-normal">(Al contado)</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Días hasta el pago</div>
                <div className="text-2xl font-bold text-teal-600">
                  {daysUntil !== null ? (
                    <>
                      {daysUntil}
                      <span className="text-base ml-1">
                        {daysUntil === 0
                          ? 'día (al contado)'
                          : daysUntil === 1
                            ? 'día'
                            : 'días'}
                      </span>
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentConditionSection;
